<template>
  <div>
    <h1 class="adsimple-311825881">Privacy Policy</h1>
    <p>
      We have written this privacy policy (version 08.09.2021-311825881) in
      order to explain to you, in accordance with the provisions of the
      <a
        class="adsimple-311825881"
        href="https://eur-lex.europa.eu/legal-content/EN/ALL/?uri=celex%3A32016R0679&amp;amp%3Btid=%5Badsimpletextid]"
        target="_blank"
        rel="noopener"
        >General Data Protection Regulation (EU) 2016/679</a
      >
      and applicable national laws, which personal data (data for short) we as
      the controller – and the processors commissioned by us (e.g. providers) –
      process, will process in the future and what legal options you have. The
      terms used are to be considered as gender-neutral.<br />
      <strong class="adsimple-311825881">In short:</strong> We provide you with
      comprehensive information about any personal data we process about you.
    </p>
    <p>
      Privacy policies usually sound very technical and use legal terminology.
      However, this privacy policy is intended to describe the most important
      things to you as simply and transparently as possible. So long as it aids
      transparency, technical
      <strong class="adsimple-311825881"
        >terms are explained in a reader-friendly manner, links</strong
      >
      to further information are provided and
      <strong class="adsimple-311825881">graphics</strong> are used. We are thus
      informing in clear and simple language that we only process personal data
      in the context of our business activities if there is a legal basis for
      it. This is certainly not possible with brief, unclear and legal-technical
      statements, as is often standard on the Internet when it comes to data
      protection. I hope you find the following explanations interesting and
      informative. Maybe you will also find some information that you have not
      been familiar with.<br />
      If you still have questions, we would like to ask you to contact the
      responsible body named below or in the imprint, to follow the existing
      links and to look at further information on third-party sites. You can of
      course also find our contact details in the imprint.
    </p>
    <h2 id="anwendungsbereich-en" class="adsimple-311825881">Scope</h2>
    <p>
      This privacy policy applies to all personal data processed by our company
      and to all personal data processed by companies commissioned by us
      (processors). With the term personal data, we refer to information within
      the meaning of Article 4 No. 1 GDPR, such as the name, email address and
      postal address of a person. The processing of personal data ensures that
      we can offer and invoice our services and products, be it online or
      offline. The scope of this privacy policy includes:
    </p>
    <ul class="adsimple-311825881">
      <li class="adsimple-311825881">
        all online presences (websites, online shops) that we operate
      </li>
      <li class="adsimple-311825881">
        Social media presences and email communication
      </li>
      <li class="adsimple-311825881">
        mobile apps for smartphones and other devices
      </li>
    </ul>
    <p>
      <strong class="adsimple-311825881">In short:</strong> This privacy policy
      applies to all areas in which personal data is processed in a structured
      manner by the company via the channels mentioned. Should we enter into
      legal relations with you outside of these channels, we will inform you
      separately if necessary.
    </p>
    <h2 id="rechtsgrundlagen-en" class="adsimple-311825881">Legal bases</h2>
    <p>
      In the following privacy policy, we provide you with transparent
      information on the legal principles and regulations, i.e. the legal bases
      of the General Data Protection Regulation, which enable us to process
      personal data.<br />
      Whenever EU law is concerned, we refer to REGULATION (EU) 2016/679 OF THE
      EUROPEAN PARLIAMENT AND OF THE COUNCIL of April 27, 2016. You can of
      course access the General Data Protection Regulation of the EU online at
      EUR-Lex, the gateway to EU law, at
      <a
        class="adsimple-311825881"
        href="https://eur-lex.europa.eu/legal-content/DE/ALL/?uri=celex%3A32016R0679&amp;tid=311825881"
        target="_blank"
        rel="noopener"
        >https://eur-lex.europa.eu/legal-content/EN/TXT/?uri=celex%3A32016R0679</a
      >.
    </p>
    <p>
      We only process your data if at least one of the following conditions
      applies:
    </p>
    <ol>
      <li class="adsimple-311825881">
        <strong class="adsimple-311825881">Consent</strong> (Article 6 Paragraph
        1 lit. a GDPR): You have given us your consent to process data for a
        specific purpose. An example would be the storage of data you entered
        into a contact form.
      </li>
      <li class="adsimple-311825881">
        <strong class="adsimple-311825881">Contract</strong> (Article 6
        Paragraph 1 lit. b GDPR): We process your data in order to fulfill a
        contract or pre-contractual obligations with you. For example, if we
        conclude a sales contract with you, we need personal information in
        advance.
      </li>
      <li class="adsimple-311825881">
        <strong class="adsimple-311825881">Legal obligation</strong> (Article 6
        Paragraph 1 lit. c GDPR): If we are subject to a legal obligation, we
        will process your data. For example, we are legally required to keep
        invoices for our bookkeeping. These usually contain personal data.
      </li>
      <li class="adsimple-311825881">
        <strong class="adsimple-311825881">Legitimate interests</strong>
        (Article 6 Paragraph 1 lit. f GDPR): In the case of legitimate interests
        that do not restrict your basic rights, we reserve the right to process
        personal data. For example, we have to process certain data in order to
        be able to operate our website securely and economically. Therefore, the
        processing is a legitimate interest.
      </li>
    </ol>
    <p>
      Other conditions such as making recordings in the interest of the public,
      the exercise of official authority as well as the protection of vital
      interests do not usually occur with us. Should such a legal basis be
      relevant, it will be disclosed in the appropriate place.
    </p>
    <p>In addition to the EU regulation, national laws also apply:</p>
    <ul class="adsimple-311825881">
      <li class="adsimple-311825881">
        In <strong class="adsimple-311825881">Austria</strong> this is the
        Austrian Data Protection Act (<strong class="adsimple-311825881"
          >Datenschutzgesetz</strong
        >), in short <strong class="adsimple-311825881">DSG</strong>.
      </li>
      <li class="adsimple-311825881">
        In <strong class="adsimple-311825881">Germany</strong> this is the
        Federal Data Protection Act (<strong class="adsimple-311825881"
          >Bundesdatenschutzgesetz</strong
        >), in short <strong class="adsimple-311825881">BDSG</strong>.
      </li>
    </ul>
    <p>
      Should other regional or national laws apply, we will inform you about
      them in the following sections.
    </p>
    <h2 id="kontaktdaten-verantwortliche-en" class="adsimple-311825881">
      Contact details of the data protection controller
    </h2>
    <p>
      If you have any questions about data protection, you will find the contact
      details of the responsible person or controller below:<br />
      Paradise Studio<br />
      co. Michaela Pearson<br />
      Merseburger Str. 120<br />
      04177 Leipzig, DE<br />
      <span class="adsimple-311825881" style="font-weight: 400"
        >Authorised to represent: Michaela Pearson</span
      >
      <br />
      Email:
      <a class="adsimple-311825881" href="mailto:hallo@paradisestudio.de"
        >hallo@paradisestudio.de</a
      >
      <br />
      Phone: 01625790189<br />
      Company details: <a class="adsimple-311825881" href=""> </a>
    </p>
    <h2 id="speicherdauer-en" class="adsimple-311825881">Storage Period</h2>
    <p>
      It is a general criterion for us to store personal data only for as long
      as is absolutely necessary for the provision of our services and products.
      This means that we delete personal data as soon as any reason for the data
      processing no longer exists. In some cases, we are legally obliged to keep
      certain data stored even after the original purpose no longer exists, such
      as for accounting purposes.
    </p>
    <p>
      If you want your data to be deleted or if you want to revoke your consent
      to data processing, the data will be deleted as soon as possible, provided
      there is no obligation to continue its storage.
    </p>
    <p>
      We will inform you below about the specific duration of the respective
      data processing, provided we have further information.
    </p>
    <h2 id="rechte-dsgvo-en" class="adsimple-311825881">
      Rights in accordance with the General Data Protection Regulation
    </h2>
    <p>
      You are granted the following rights in accordance with the provisions of
      the
      <a
        class="adsimple-311825881"
        href="https://gdpr-info.eu/"
        target="_blank"
        rel="noopener"
        >GDPR</a
      >
      (General Data Protection Regulation) and the Austrian
      <a
        class="adsimple-311825881"
        href="https://www.ris.bka.gv.at/Dokumente/Erv/ERV_1999_1_165/ERV_1999_1_165.html"
        target="_blank"
        rel="noopener"
        >Data Protection Act (DSG)</a
      >:
    </p>
    <ul class="adsimple-311825881">
      <li class="adsimple-311825881">
        right to rectification (article 16 GDPR)
      </li>
      <li class="adsimple-311825881">
        right to erasure (“right to be forgotten“) (article 17 GDPR)
      </li>
      <li class="adsimple-311825881">
        right to restrict processing (article 18 GDPR)
      </li>
      <li class="adsimple-311825881">
        righ to notification – notification obligation regarding rectification
        or erasure of personal data or restriction of processing (article 19
        GDPR)
      </li>
      <li class="adsimple-311825881">
        right to data portability (article 20 GDPR)
      </li>
      <li class="adsimple-311825881">Right to object (article 21 GDPR)</li>
      <li class="adsimple-311825881">
        right not to be subject to a decision based solely on automated
        processing – including profiling – (article 22 GDPR)
      </li>
    </ul>
    <p>
      If you think that the processing of your data violates the data protection
      law, or that your data protection rights have been infringed in any other
      way, you can lodge a complaint with your respective regulatory authority.
      For Austria this is the data protection authority, whose website you can
      access at
      <a
        class="adsimple-311825881"
        href="https://www.data-protection-authority.gv.at/?tid=311825881"
        >https://www.data-protection-authority.gv.at/</a
      >.
    </p>
    <h2 id="sachsen-datenschutzbehoerde-en" class="adsimple-311825881">
      Sachsen Data protection authority
    </h2>
    <p>
      <strong class="adsimple-311825881"
        >State Commissioner for Data Protection:</strong
      >
      Andreas Schurig<br />
      <strong class="adsimple-311825881">Address: </strong>Devrientstraße 5,
      01067 Dresden<br />
      <strong class="adsimple-311825881">Phone number:</strong> 03 51 /
      85471-101<br />
      <strong class="adsimple-311825881">E-mail address:</strong>
      saechsdsb@slt.sachsen.de<br />
      <strong class="adsimple-311825881">Website: </strong>
      <a
        class="adsimple-311825881"
        href="https://www.saechsdsb.de/?tid=311825881"
        target="_blank"
        rel="noopener"
        >https://www.saechsdsb.de/</a
      >
    </p>
    <h2 id="webhosting-en" class="adsimple-311825881">Web hosting</h2>
    <table border="1" cellpadding="15">
      <tbody>
        <tr>
          <td>
            <strong class="adsimple-311825881">Web hosting Overview</strong>
            <br />
            👥 Affected parties: visitors to the website<br />
            🤝 Purpose: professional hosting of the website and security of
            operations<br />
            📓 Processed data: IP address, time of website visit, browser used
            and other data. You can find more details on this below or at the
            respective web hosting provider.<br />
            📅 Storage period: dependent on the respective provider, but usually
            2 weeks<br />
            ⚖️ Legal basis: Art. 6 para. 1 lit. f GDPR (legitimate interests)
          </td>
        </tr>
      </tbody>
    </table>
    <h3 class="adsimple-311825881">What is web hosting?</h3>
    <p>
      Every time you visit a website nowadays, certain information &#8211;
      including personal data &#8211; is automatically created and stored,
      including on this website. This data should be processed as sparingly as
      possible, and only with good reason. By website, we mean the entirety of
      all websites on your domain, i.e. everything from the homepage to the very
      last subpage (like this one here). By domain we mean example.uk or
      examplepage.com.
    </p>
    <p>
      When you want to view a website on a screen, you use a program called a
      web browser. You probably know the names of some web browsers: Google
      Chrome, Microsoft Edge, Mozilla Firefox, and Apple Safari.
    </p>
    <p>
      The web browser has to connect to another computer which stores the
      website&#8217;s code: the web server. Operating a web server is
      complicated and time-consuming, which is why this is usually done by
      professional providers. They offer web hosting and thus ensure the
      reliable and flawless storage of website data.
    </p>
    <p>
      Whenever the browser on your computer establishes a connection (desktop,
      laptop, smartphone) and whenever data is being transferred to and from the
      web server, personal data may be processed. After all, your computer
      stores data, and the web server also has to retain the data for a period
      of time in order to ensure it can operate properly.
    </p>
    <p>Illustration:</p>
    <p>
      <img
        role="img"
        src="https://www.adsimple.at/wp-content/uploads/2018/03/browser-und-webserver.svg"
        alt="Browser and web server"
        width="100% "
      />
    </p>
    <h3 class="adsimple-311825881">Why do we process personal data?</h3>
    <p>The purposes of data processing are:</p>
    <ol>
      <li class="adsimple-311825881">
        Professional hosting of the website and operational security
      </li>
      <li class="adsimple-311825881">
        To maintain the operational as well as IT security
      </li>
      <li class="adsimple-311825881">
        Anonymous evaluation of access patterns to improve our offer, and if
        necessary, for prosecution or the pursuit of claims.li&gt;
      </li>
    </ol>
    <h3 class="adsimple-311825881">Which data are processed?</h3>
    <p>
      Even while you are visiting our website, our web server, that is the
      computer on which this website is saved, usually automatically saves data
      such as
    </p>
    <ul class="adsimple-311825881">
      <li class="adsimple-311825881">
        the full address (URL) of the accessed website (e. g.
        https://www.examplepage.uk/examplesubpage.html?tid=311825881)
      </li>
      <li class="adsimple-311825881">
        browser and browser version (e.g. Chrome 87)
      </li>
      <li class="adsimple-311825881">
        the operating system used (e.g. Windows 10)
      </li>
      <li class="adsimple-311825881">
        the address (URL) of the previously visited page (referrer URL) (e. g.
        https://www.examplepage.uk/icamefromhere.html/)
      </li>
      <li class="adsimple-311825881">
        the host name and the IP address of the device from the website is being
        accessed from (e.g. COMPUTERNAME and 194.23.43.121)
      </li>
      <li class="adsimple-311825881">date and time</li>
      <li class="adsimple-311825881">in so-called web server log files</li>
    </ul>
    <h3 class="adsimple-311825881">How long is the data stored?</h3>
    <p>
      Generally, the data mentioned above are stored for two weeks and are then
      automatically deleted. We do not pass these data on to others, but we
      cannot rule out the possibility that this data may be viewed by the
      authorities in the event of illegal conduct.
    </p>
    <p>
      <strong class="adsimple-311825881">In short:</strong> Your visit is logged
      by our provider (company that runs our website on special computers
      (servers)), but we do not pass on your data without your consent!
    </p>
    <h3 class="adsimple-311825881">Legal basis</h3>
    <p>
      The lawfulness of processing personal data in the context of web hosting
      is justified in Art. 6 para. 1 lit. f GDPR (safeguarding of legitimate
      interests), as the use of professional hosting with a provider is
      necessary to present the company in a safe and user-friendly manner on the
      internet, as well as to have the ability to track any attacks and claims,
      if necessary.
    </p>
    <h2 id="1und1-ionos-datenschutzerklaerung-en" class="adsimple-311825881">
      1&#038;1 IONOS Webhosting Privacy Policy
    </h2>
    <p>
      We use IONOS by 1&amp;1 to host our website. In Germany, 1&amp;1 IONOS SE
      is located at Elgendorfer Str. 57, 56410 Montabaur, in Austria you can
      find 1&amp;1 IONOS SE at Gumpendorfer Straße 142/PF 266, 1060 Vienna.
      IONOS offers the following web hosting services: Domain, Website &amp;
      Shop, Hosting &amp; WordPress, Marketing, Email &amp; Office, IONOS Cloud
      and Server.
    </p>
    <p>
      As explained in the &#8220;Automatic Data Storage&#8221; section, web
      servers, like those of IONOS, store data of every website visit.
    </p>
    <p>
      If you would like to learn more about IONOS website privacy, please visit
      the
      <a
        class="adsimple-311825881"
        href="https://www.ionos.de/terms-gtc/terms-privacy?tid=311825881"
        target="_blank"
        rel="follow noopener"
        >privacy policy at ionos.com.</a
      >
    </p>
    <h2 id="cookie-consent-management-platform-en" class="adsimple-311825881">
      Cookie Consent Management Platform
    </h2>
    <table border="1" cellpadding="15">
      <tbody>
        <tr>
          <td>
            <strong class="adsimple-311825881">
              Cookie Consent Management Platform Overview</strong
            >
            <br />
            👥 Affected parties: Website visitors<br />
            🤝 Purpose: Obtaining and managing consent to certain cookies and
            thus the use of certain tools<br />
            📓 Processed data: data for managing cookie settings such as IP
            address, time of consent, type of consent and individual consent.
            You can find more details on this directly with the tool that is
            being used.<br />
            📅 Storage period: depends on the tool used, periods of several
            years can be assumed<br />
            ⚖️ Legal bases: Art. 6 para. 1 lit. a GDPR (consent), Art. 6 para. 1
            lit. f GDPR (legitimate interests)
          </td>
        </tr>
      </tbody>
    </table>
    <h3 class="adsimple-311825881">
      What is a cookie consent management platform?
    </h3>
    <p>
      We use a Consent Management Platform (CMP) software on our website that
      makes it easier for us and you to handle the scripts and cookies used
      correctly and securely. The software automatically creates a cookie
      pop-up, scans and controls all scripts and cookies, provides you with the
      cookie consent required under data protection law and helps you and us to
      keep track of all cookies. Most cookie consent management tools identify
      and categorize all existing cookies. As a website visitor, you then decide
      for yourself whether and which scripts and cookies you allow or not. The
      following graphic shows the relationship between browser, web server and
      CMP.
    </p>
    <p>
      <img
        role="img"
        src="https://www.adsimple.at/wp-content/uploads/2021/03/consent-management-platform-overview.svg"
        alt="Consent Management Platform overview"
        width="100%"
      />
    </p>
    <h3 class="adsimple-311825881">Why do we use a cookie management tool?</h3>
    <p>
      Our goal is to offer you the best possible transparency in the area of
      data protection. We are also legally obliged to do so. We want to inform
      you as well as possible about all tools and all cookies that can save and
      process your data. It is also your right to decide for yourself which
      cookies you accept and which you do not. In order to grant you this right,
      we first need to know exactly which cookies actually landed on our
      website. Thanks to a cookie management tool, which regularly scans the
      website for all cookies present, we know about all cookies and can provide
      you with GDPR-compliant information. You can then use the consent system
      to accept or reject cookies.
    </p>
    <h3 class="adsimple-311825881">Which data are processed?</h3>
    <p>
      As part of our cookie management tool, you can manage each individual
      cookie yourself and have complete control over the storage and processing
      of your data. The declaration of your consent is stored so that we do not
      have to ask you every time you visit our website and we can also prove
      your consent if required by law. This is saved either in an opt-in cookie
      or on a server. The storage time of your cookie consent varies depending
      on the provider of the cookie management tool. Usually this data (e.g.
      pseudonymous user ID, time of consent, detailed information on the cookie
      categories or tools, browser, device information) is stored for up to two
      years.
    </p>
    <h3 class="adsimple-311825881">Duration of data processing</h3>
    <p>
      We will inform you below about the duration of the data processing if we
      have further information. In general, we only process personal data for as
      long as is absolutely necessary for the provision of our services and
      products. Data stored in cookies are stored for different lengths of time.
      Some cookies are deleted after you leave the website, others may be stored
      in your browser for a few years. The exact duration of the data processing
      depends on the tool used, in most cases you should be prepared for a
      storage period of several years. In the respective data protection
      declarations of the individual providers, you will usually receive precise
      information about the duration of the data processing.
    </p>
    <h3 class="adsimple-311825881">Right of objection</h3>
    <p>
      You also have the right and the option to revoke your consent to the use
      of cookies at any time. This works either via our cookie management tool
      or via other opt-out functions. For example, you can also prevent data
      collection by cookies by managing, deactivating or deleting cookies in
      your browser.
    </p>
    <p>
      Information on special cookie management tools can be found &#8211; if
      available &#8211; in the following sections.
    </p>
    <h3 class="adsimple-311825881">Legal basis</h3>
    <p>
      If you agree to cookies, your personal data will be processed and stored
      via these cookies. If we are allowed to use cookies with your
      <strong class="adsimple-311825881"> consent </strong> (Article 6 paragraph
      1 lit. a GDPR), this consent is also the legal basis for the use of
      cookies and the processing of your data. In order to be able to manage the
      consent to cookies and to enable you to give your consent, a cookie
      consent management platform software is used. The use of this software
      enables us to operate the website in an efficient and legally compliant
      manner, which is a
      <strong class="adsimple-311825881"> legitimate interest </strong> (Article
      6 paragraph 1 lit. f GDPR).
    </p>
    <p>All texts are copyrighted.</p>
    <p style="margin-top: 15px">
      Source: Created with the
      <a
        href="https://www.adsimple.de/datenschutz-generator/"
        title="Datenschutz Generator by AdSimple for Germany"
        >Datenschutz Generator</a
      >
      by AdSimple
    </p>

    <h1 class="adsimple-311825881">Datenschutzerklärung</h1>
    <h2 id="einleitung-ueberblick" class="adsimple-311825881">
      Einleitung und Überblick
    </h2>
    <p>
      Wir haben diese Datenschutzerklärung (Fassung 08.09.2021-311825881)
      verfasst, um Ihnen gemäß der Vorgaben der
      <a
        class="adsimple-311825881"
        href="https://eur-lex.europa.eu/legal-content/DE/ALL/?uri=celex%3A32016R0679&amp;tid=311825881"
        target="_blank"
        rel="noopener"
        >Datenschutz-Grundverordnung (EU) 2016/679</a
      >
      und anwendbaren nationalen Gesetzen zu erklären, welche personenbezogenen
      Daten (kurz Daten) wir als Verantwortliche &#8211; und die von uns
      beauftragten Auftragsverarbeiter (z. B. Provider) &#8211; verarbeiten,
      zukünftig verarbeiten werden und welche rechtmäßigen Möglichkeiten Sie
      haben. Die verwendeten Begriffe sind geschlechtsneutral zu verstehen.<br />
      <strong class="adsimple-311825881">Kurz gesagt:</strong> Wir informieren
      Sie umfassend über Daten, die wir über Sie verarbeiten.
    </p>
    <p>
      Datenschutzerklärungen klingen für gewöhnlich sehr technisch und verwenden
      juristische Fachbegriffe. Diese Datenschutzerklärung soll Ihnen hingegen
      die wichtigsten Dinge so einfach und transparent wie möglich beschreiben.
      Soweit es der Transparenz förderlich ist, werden technische
      <strong class="adsimple-311825881"
        >Begriffe leserfreundlich erklärt</strong
      >, <strong class="adsimple-311825881">Links</strong> zu weiterführenden
      Informationen geboten und
      <strong class="adsimple-311825881">Grafiken</strong> zum Einsatz gebracht.
      Wir informieren damit in klarer und einfacher Sprache, dass wir im Rahmen
      unserer Geschäftstätigkeiten nur dann personenbezogene Daten verarbeiten,
      wenn eine entsprechende gesetzliche Grundlage gegeben ist. Das ist sicher
      nicht möglich, wenn man möglichst knappe, unklare und
      juristisch-technische Erklärungen abgibt, so wie sie im Internet oft
      Standard sind, wenn es um Datenschutz geht. Ich hoffe, Sie finden die
      folgenden Erläuterungen interessant und informativ und vielleicht ist die
      eine oder andere Information dabei, die Sie noch nicht kannten.<br />
      Wenn trotzdem Fragen bleiben, möchten wir Sie bitten, sich an die unten
      bzw. im Impressum genannte verantwortliche Stelle zu wenden, den
      vorhandenen Links zu folgen und sich weitere Informationen auf Drittseiten
      anzusehen. Unsere Kontaktdaten finden Sie selbstverständlich auch im
      Impressum.
    </p>
    <h2 id="anwendungsbereich" class="adsimple-311825881">Anwendungsbereich</h2>
    <p>
      Diese Datenschutzerklärung gilt für alle von uns im Unternehmen
      verarbeiteten personenbezogenen Daten und für alle personenbezogenen
      Daten, die von uns beauftragte Firmen (Auftragsverarbeiter) verarbeiten.
      Mit personenbezogenen Daten meinen wir Informationen im Sinne des Art. 4
      Nr. 1 DSGVO wie zum Beispiel Name, E-Mail-Adresse und postalische
      Anschrift einer Person. Die Verarbeitung personenbezogener Daten sorgt
      dafür, dass wir unsere Dienstleistungen und Produkte anbieten und
      abrechnen können, sei es online oder offline. Der Anwendungsbereich dieser
      Datenschutzerklärung umfasst:
    </p>
    <ul class="adsimple-311825881">
      <li class="adsimple-311825881">
        alle Onlineauftritte (Websites, Onlineshops), die wir betreiben
      </li>
      <li class="adsimple-311825881">
        Social Media Auftritte und E-Mail-Kommunikation
      </li>
      <li class="adsimple-311825881">
        mobile Apps für Smartphones und andere Geräte
      </li>
    </ul>
    <p>
      <strong class="adsimple-311825881">Kurz gesagt:</strong> Die
      Datenschutzerklärung gilt für alle Bereiche, in denen personenbezogene
      Daten im Unternehmen über die genannten Kanäle strukturiert verarbeitet
      werden. Sollten wir außerhalb dieser Kanäle mit Ihnen in Rechtsbeziehungen
      eintreten, werden wir Sie gegebenenfalls gesondert informieren.
    </p>
    <h2 id="rechtsgrundlagen" class="adsimple-311825881">Rechtsgrundlagen</h2>
    <p>
      In der folgenden Datenschutzerklärung geben wir Ihnen transparente
      Informationen zu den rechtlichen Grundsätzen und Vorschriften, also den
      Rechtsgrundlagen der Datenschutz-Grundverordnung, die uns ermöglichen,
      personenbezogene Daten zu verarbeiten.<br />
      Was das EU-Recht betrifft, beziehen wir uns auf die VERORDNUNG (EU)
      2016/679 DES EUROPÄISCHEN PARLAMENTS UND DES RATES vom 27. April 2016.
      Diese Datenschutz-Grundverordnung der EU können Sie selbstverständlich
      online auf EUR-Lex, dem Zugang zum EU-Recht, unter
      <a
        class="adsimple-311825881"
        href="https://eur-lex.europa.eu/legal-content/DE/ALL/?uri=celex%3A32016R0679&amp;tid=311825881"
        target="_blank"
        rel="noopener"
        >https://eur-lex.europa.eu/legal-content/DE/TXT/?uri=celex%3A32016R0679</a
      >
      nachlesen.
    </p>
    <p>
      Wir verarbeiten Ihre Daten nur, wenn mindestens eine der folgenden
      Bedingungen zutrifft:
    </p>
    <ol>
      <li class="adsimple-311825881">
        <strong class="adsimple-311825881">Einwilligung</strong> (Artikel 6
        Absatz 1 lit. a DSGVO): Sie haben uns Ihre Einwilligung gegeben, Daten
        zu einem bestimmten Zweck zu verarbeiten. Ein Beispiel wäre die
        Speicherung Ihrer eingegebenen Daten eines Kontaktformulars.
      </li>
      <li class="adsimple-311825881">
        <strong class="adsimple-311825881">Vertrag</strong> (Artikel 6 Absatz 1
        lit. b DSGVO): Um einen Vertrag oder vorvertragliche Verpflichtungen mit
        Ihnen zu erfüllen, verarbeiten wir Ihre Daten. Wenn wir zum Beispiel
        einen Kaufvertrag mit Ihnen abschließen, benötigen wir vorab
        personenbezogene Informationen.
      </li>
      <li class="adsimple-311825881">
        <strong class="adsimple-311825881">Rechtliche Verpflichtung</strong>
        (Artikel 6 Absatz 1 lit. c DSGVO): Wenn wir einer rechtlichen
        Verpflichtung unterliegen, verarbeiten wir Ihre Daten. Zum Beispiel sind
        wir gesetzlich verpflichtet Rechnungen für die Buchhaltung aufzuheben.
        Diese enthalten in der Regel personenbezogene Daten.
      </li>
      <li class="adsimple-311825881">
        <strong class="adsimple-311825881">Berechtigte Interessen</strong>
        (Artikel 6 Absatz 1 lit. f DSGVO): Im Falle berechtigter Interessen, die
        Ihre Grundrechte nicht einschränken, behalten wir uns die Verarbeitung
        personenbezogener Daten vor. Wir müssen zum Beispiel gewisse Daten
        verarbeiten, um unsere Website sicher und wirtschaftlich effizient
        betreiben zu können. Diese Verarbeitung ist somit ein berechtigtes
        Interesse.
      </li>
    </ol>
    <p>
      Weitere Bedingungen wie die Wahrnehmung von Aufnahmen im öffentlichen
      Interesse und Ausübung öffentlicher Gewalt sowie dem Schutz
      lebenswichtiger Interessen treten bei uns in der Regel nicht auf. Soweit
      eine solche Rechtsgrundlage doch einschlägig sein sollte, wird diese an
      der entsprechenden Stelle ausgewiesen.
    </p>
    <p>Zusätzlich zu der EU-Verordnung gelten auch noch nationale Gesetze:</p>
    <ul class="adsimple-311825881">
      <li class="adsimple-311825881">
        In <strong class="adsimple-311825881">Österreich</strong> ist dies das
        Bundesgesetz zum Schutz natürlicher Personen bei der Verarbeitung
        personenbezogener Daten (<strong class="adsimple-311825881"
          >Datenschutzgesetz</strong
        >), kurz <strong class="adsimple-311825881">DSG</strong>.
      </li>
      <li class="adsimple-311825881">
        In <strong class="adsimple-311825881">Deutschland</strong> gilt das
        <strong class="adsimple-311825881">Bundesdatenschutzgesetz</strong>,
        kurz<strong class="adsimple-311825881"> BDSG</strong>.
      </li>
    </ul>
    <p>
      Sofern weitere regionale oder nationale Gesetze zur Anwendung kommen,
      informieren wir Sie in den folgenden Abschnitten darüber.
    </p>
    <h2 id="kontaktdaten-verantwortliche" class="adsimple-311825881">
      Kontaktdaten des Verantwortlichen
    </h2>
    <p>
      Sollten Sie Fragen zum Datenschutz haben, finden Sie nachfolgend die
      Kontaktdaten der verantwortlichen Person bzw. Stelle:<br />
      <span class="adsimple-311825881" style="font-weight: 400"
        >Paradise Studio<br />
        co. Michaela Pearson<br />
        Merseburger Str. 120<br />
        04177 Leipzig, DE</span
      >
      <br />
      Vertretungsberechtigt: Michaela Pearson<br />
      E-Mail:
      <a class="adsimple-311825881" href="mailto:hallo@paradisestudio.de"
        >hallo@paradisestudio.de</a
      >
      <br />
      Telefon: 01625790189<br />
      Impressum: <a class="adsimple-311825881" href=""> </a>
    </p>
    <h2 id="speicherdauer" class="adsimple-311825881">Speicherdauer</h2>
    <p>
      Dass wir personenbezogene Daten nur so lange speichern, wie es für die
      Bereitstellung unserer Dienstleistungen und Produkte unbedingt notwendig
      ist, gilt als generelles Kriterium bei uns. Das bedeutet, dass wir
      personenbezogene Daten löschen, sobald der Grund für die Datenverarbeitung
      nicht mehr vorhanden ist. In einigen Fällen sind wir gesetzlich dazu
      verpflichtet, bestimmte Daten auch nach Wegfall des ursprüngliches Zwecks
      zu speichern, zum Beispiel zu Zwecken der Buchführung.
    </p>
    <p>
      Sollten Sie die Löschung Ihrer Daten wünschen oder die Einwilligung zur
      Datenverarbeitung widerrufen, werden die Daten so rasch wie möglich und
      soweit keine Pflicht zur Speicherung besteht, gelöscht.
    </p>
    <p>
      Über die konkrete Dauer der jeweiligen Datenverarbeitung informieren wir
      Sie weiter unten, sofern wir weitere Informationen dazu haben.
    </p>
    <h2 id="rechte-dsgvo" class="adsimple-311825881">
      Rechte laut Datenschutz-Grundverordnung
    </h2>
    <p>
      Laut Artikel 13 DSGVO stehen Ihnen die folgenden Rechte zu, damit es zu
      einer fairen und transparenten Verarbeitung von Daten kommt:
    </p>
    <ul class="adsimple-311825881">
      <li class="adsimple-311825881">
        Sie haben laut Artikel 15 DSGVO ein Auskunftsrecht darüber, ob wir Daten
        von Ihnen verarbeiten. Sollte das zutreffen, haben Sie Recht darauf eine
        Kopie der Daten zu erhalten und die folgenden Informationen zu erfahren:
        <ul class="adsimple-311825881">
          <li class="adsimple-311825881">
            zu welchem Zweck wir die Verarbeitung durchführen;
          </li>
          <li class="adsimple-311825881">
            die Kategorien, also die Arten von Daten, die verarbeitet werden;
          </li>
          <li class="adsimple-311825881">
            wer diese Daten erhält und wenn die Daten an Drittländer übermittelt
            werden, wie die Sicherheit garantiert werden kann;
          </li>
          <li class="adsimple-311825881">
            wie lange die Daten gespeichert werden;
          </li>
          <li class="adsimple-311825881">
            das Bestehen des Rechts auf Berichtigung, Löschung oder
            Einschränkung der Verarbeitung und dem Widerspruchsrecht gegen die
            Verarbeitung;
          </li>
          <li class="adsimple-311825881">
            dass Sie sich bei einer Aufsichtsbehörde beschweren können (Links zu
            diesen Behörden finden Sie weiter unten);
          </li>
          <li class="adsimple-311825881">
            die Herkunft der Daten, wenn wir sie nicht bei Ihnen erhoben haben;
          </li>
          <li class="adsimple-311825881">
            ob Profiling durchgeführt wird, ob also Daten automatisch
            ausgewertet werden, um zu einem persönlichen Profil von Ihnen zu
            gelangen.
          </li>
        </ul>
      </li>
      <li class="adsimple-311825881">
        Sie haben laut Artikel 16 DSGVO ein Recht auf Berichtigung der Daten,
        was bedeutet, dass wir Daten richtig stellen müssen, falls Sie Fehler
        finden.
      </li>
      <li class="adsimple-311825881">
        Sie haben laut Artikel 17 DSGVO das Recht auf Löschung („Recht auf
        Vergessenwerden“), was konkret bedeutet, dass Sie die Löschung Ihrer
        Daten verlangen dürfen.
      </li>
      <li class="adsimple-311825881">
        Sie haben laut Artikel 18 DSGVO das Recht auf Einschränkung der
        Verarbeitung, was bedeutet, dass wir die Daten nur mehr speichern dürfen
        aber nicht weiter verwenden.
      </li>
      <li class="adsimple-311825881">
        Sie haben laut Artikel 19 DSGVO das Recht auf Datenübertragbarkeit, was
        bedeutet, dass wir Ihnen auf Anfrage Ihre Daten in einem gängigen Format
        zur Verfügung stellen.
      </li>
      <li class="adsimple-311825881">
        Sie haben laut Artikel 21 DSGVO ein Widerspruchsrecht, welches nach
        Durchsetzung eine Änderung der Verarbeitung mit sich bringt.
        <ul class="adsimple-311825881">
          <li class="adsimple-311825881">
            Wenn die Verarbeitung Ihrer Daten auf Artikel 6 Abs. 1 lit. e
            (öffentliches Interesse, Ausübung öffentlicher Gewalt) oder Artikel
            6 Abs. 1 lit. f (berechtigtes Interesse) basiert, können Sie gegen
            die Verarbeitung Widerspruch einlegen. Wir prüfen danach so rasch
            wie möglich, ob wir diesem Widerspruch rechtlich nachkommen können.
          </li>
          <li class="adsimple-311825881">
            Werden Daten verwendet, um Direktwerbung zu betreiben, können Sie
            jederzeit gegen diese Art der Datenverarbeitung widersprechen. Wir
            dürfen Ihre Daten danach nicht mehr für Direktmarketing verwenden.
          </li>
          <li class="adsimple-311825881">
            Werden Daten verwendet, um Profiling zu betreiben, können Sie
            jederzeit gegen diese Art der Datenverarbeitung widersprechen. Wir
            dürfen Ihre Daten danach nicht mehr für Profiling verwenden.
          </li>
        </ul>
      </li>
      <li class="adsimple-311825881">
        Sie haben laut Artikel 22 DSGVO unter Umständen das Recht, nicht einer
        ausschließlich auf einer automatisierten Verarbeitung (zum Beispiel
        Profiling) beruhenden Entscheidung unterworfen zu werden.
      </li>
    </ul>
    <p>
      <strong class="adsimple-311825881">Kurz gesagt:</strong> Sie haben Rechte
      &#8211; zögern Sie nicht, die oben gelistete verantwortliche Stelle bei
      uns zu kontaktieren!
    </p>
    <p>
      Wenn Sie glauben, dass die Verarbeitung Ihrer Daten gegen das
      Datenschutzrecht verstößt oder Ihre datenschutzrechtlichen Ansprüche in
      sonst einer Weise verletzt worden sind, können Sie sich bei der
      Aufsichtsbehörde beschweren. Diese ist für Österreich die
      Datenschutzbehörde, deren Website Sie unter
      <a
        class="adsimple-311825881"
        href="https://www.dsb.gv.at/?tid=311825881"
        target="_blank"
        rel="noopener"
        >https://www.dsb.gv.at/</a
      >
      finden. In Deutschland gibt es für jedes Bundesland einen
      Datenschutzbeauftragten. Für nähere Informationen können Sie sich an die
      <a
        class="adsimple-311825881"
        href="https://www.bfdi.bund.de"
        target="_blank"
        rel="noopener"
        >Bundesbeauftragte für den Datenschutz und die Informationsfreiheit
        (BfDI)</a
      >
      wenden. Für unser Unternehmen ist die folgende lokale Datenschutzbehörde
      zuständig:
    </p>
    <h2 id="sachsen-datenschutzbehoerde" class="adsimple-311825881">
      Sachsen Datenschutzbehörde
    </h2>
    <p>
      <strong class="adsimple-311825881"
        >Landesbeauftragter für Datenschutz:</strong
      >
      Andreas Schurig<br />
      <strong class="adsimple-311825881">Adresse: </strong>Devrientstraße 5,
      01067 Dresden<br />
      <strong class="adsimple-311825881">Telefonnr.:</strong> 03 51 /
      85471-101<br />
      <strong class="adsimple-311825881">E-Mail-Adresse:</strong>
      saechsdsb@slt.sachsen.de<br />
      <strong class="adsimple-311825881">Website: </strong>
      <a
        class="adsimple-311825881"
        href="https://www.saechsdsb.de/?tid=311825881"
        target="_blank"
        rel="noopener"
        >https://www.saechsdsb.de/</a
      >
    </p>
    <h2 id="webhosting" class="adsimple-311825881">Webhosting</h2>
    <table border="1" cellpadding="15">
      <tbody>
        <tr>
          <td>
            <strong class="adsimple-311825881"
              >Webhosting Zusammenfassung</strong
            >
            <br />
            &#x1f465; Betroffene: Besucher der Website<br />
            &#x1f91d; Zweck: professionelles Hosting der Website und Absicherung
            des Betriebs<br />
            &#x1f4d3; Verarbeitete Daten: IP-Adresse, Zeitpunkt des
            Websitebesuchs, verwendeter Browser und weitere Daten. Mehr Details
            dazu finden Sie weiter unten bzw. beim jeweils eingesetzten
            Webhosting Provider.<br />
            &#x1f4c5; Speicherdauer: abhängig vom jeweiligen Provider, aber in
            der Regel 2 Wochen<br />
            &#x2696;&#xfe0f; Rechtsgrundlagen: Art. 6 Abs. 1 lit.f DSGVO
            (Berechtigte Interessen)
          </td>
        </tr>
      </tbody>
    </table>
    <h3 class="adsimple-311825881">Was ist Webhosting?</h3>
    <p>
      Wenn Sie heutzutage Websites besuchen, werden gewisse Informationen
      &#8211; auch personenbezogene Daten &#8211; automatisch erstellt und
      gespeichert, so auch auf dieser Website. Diese Daten sollten möglichst
      sparsam und nur mit Begründung verarbeitet werden. Mit Website meinen wir
      übrigens die Gesamtheit aller Webseiten auf einer Domain, d.h. alles von
      der Startseite (Homepage) bis hin zur aller letzten Unterseite (wie dieser
      hier). Mit Domain meinen wir zum Beispiel beispiel.de oder
      musterbeispiel.com.
    </p>
    <p>
      Wenn Sie eine Website auf einem Bildschirm ansehen möchten, verwenden Sie
      dafür ein Programm, das sich Webbrowser nennt. Sie kennen vermutlich
      einige Webbrowser beim Namen: Google Chrome, Microsoft Edge, Mozilla
      Firefox und Apple Safari.
    </p>
    <p>
      Dieser Webbrowser muss sich zu einem anderen Computer verbinden, wo der
      Code der Website gespeichert ist: dem Webserver. Der Betrieb eines
      Webservers ist eine komplizierte und aufwendige Aufgabe, weswegen dies in
      der Regel von professionellen Anbietern, den Providern, übernommen wird.
      Diese bieten Webhosting an und sorgen damit für eine verlässliche und
      fehlerfreie Speicherung der Daten von Websites.
    </p>
    <p>
      Bei der Verbindungsaufnahme des Browsers auf Ihrem Computer (Desktop,
      Laptop, Smartphone) und während der Datenübertragung zu und vom Webserver
      kann es zu einer Verarbeitung personenbezogener Daten kommen. Einerseits
      speichert Ihr Computer Daten, andererseits muss auch der Webserver Daten
      eine Zeit lang speichern, um einen ordentlichen Betrieb zu gewährleisten.
    </p>
    <p>Zur Veranschaulichung:</p>
    <p>
      <img
        role="img"
        src="https://www.adsimple.at/wp-content/uploads/2018/03/browser-und-webserver.svg"
        alt="Browser und Webserver"
        width="100%"
      />
    </p>
    <h3 class="adsimple-311825881">
      Warum verarbeiten wir personenbezogene Daten?
    </h3>
    <p>Die Zwecke der Datenverarbeitung sind:</p>
    <ol>
      <li class="adsimple-311825881">
        Professionelles Hosting der Website und Absicherung des Betriebs
      </li>
      <li class="adsimple-311825881">
        zur Aufrechterhaltung der Betriebs- und IT-Sicherheit
      </li>
      <li class="adsimple-311825881">
        Anonyme Auswertung des Zugriffsverhaltens zur Verbesserung unseres
        Angebots und ggf. zur Strafverfolgung bzw. Verfolgung von Ansprüchen
      </li>
    </ol>
    <h3 class="adsimple-311825881">Welche Daten werden verarbeitet?</h3>
    <p>
      Auch während Sie unsere Website jetzt gerade besuchen, speichert unser
      Webserver, das ist der Computer auf dem diese Webseite gespeichert ist, in
      der Regel automatisch Daten wie
    </p>
    <ul class="adsimple-311825881">
      <li class="adsimple-311825881">
        die komplette Internetadresse (URL) der aufgerufenen Webseite (z. B.
        https://www.beispielwebsite.de/beispielunterseite.html?tid=311825881)
      </li>
      <li class="adsimple-311825881">
        Browser und Browserversion (z. B. Chrome 87)
      </li>
      <li class="adsimple-311825881">
        das verwendete Betriebssystem (z. B. Windows 10)
      </li>
      <li class="adsimple-311825881">
        die Adresse (URL) der zuvor besuchten Seite (Referrer URL) (z. B.
        https://www.beispielquellsite.de/vondabinichgekommen.html/)
      </li>
      <li class="adsimple-311825881">
        den Hostnamen und die IP-Adresse des Geräts von welchem aus zugegriffen
        wird (z. B. COMPUTERNAME und 194.23.43.121)
      </li>
      <li class="adsimple-311825881">Datum und Uhrzeit</li>
      <li class="adsimple-311825881">
        in Dateien, den sogenannten Webserver-Logfiles
      </li>
    </ul>
    <h3 class="adsimple-311825881">Wie lange werden Daten gespeichert?</h3>
    <p>
      In der Regel werden die oben genannten Daten zwei Wochen gespeichert und
      danach automatisch gelöscht. Wir geben diese Daten nicht weiter, können
      jedoch nicht ausschließen, dass diese Daten beim Vorliegen von
      rechtswidrigem Verhalten von Behörden eingesehen werden.
    </p>
    <p>
      <strong class="adsimple-311825881">Kurz gesagt:</strong> Ihr Besuch wird
      durch unseren Provider (Firma, die unsere Website auf speziellen Computern
      (Servern) laufen lässt), protokolliert, aber wir geben Ihre Daten nicht
      ohne Zustimmung weiter!
    </p>
    <h3 class="adsimple-311825881">Rechtsgrundlage</h3>
    <p>
      Die Rechtmäßigkeit der Verarbeitung personenbezogener Daten im Rahmen des
      Webhosting ergibt sich aus Art. 6 Abs. 1 lit. f DSGVO (Wahrung der
      berechtigten Interessen), denn die Nutzung von professionellem Hosting bei
      einem Provider ist notwendig, um das Unternehmen im Internet sicher und
      nutzerfreundlich präsentieren und Angriffe und Forderungen hieraus
      gegebenenfalls verfolgen zu können.
    </p>
    <h2 id="1und1-ionos-datenschutzerklaerung" class="adsimple-311825881">
      1&amp;1 IONOS Webhosting Datenschutzerklärung
    </h2>
    <p>
      Wir nutzen IONOS by 1&amp;1 um unsere Website zu hosten. In Deutschland
      hat die 1&amp;1 IONOS SE ihren Sitz in Elgendorfer Str. 57, 56410
      Montabaur, in Österreich finden Sie die 1&amp;1 IONOS SE in der
      Gumpendorfer Straße 142/PF 266, 1060 Wien. IONOS bietet folgende
      Leistungen rund um Webhosting an: Domain, Website &amp; Shop, Hosting
      &amp; WordPress, Marketing, E-Mail &amp; Office, IONOS Cloud und Server.
    </p>
    <p>
      Wie im Abschnitt &#8220;Automatische Datenspeicherung&#8221; erläutert,
      speichern Webserver, wie die von IONOS, Daten jedes Websitebesuchs ab.
    </p>
    <p>
      Wenn Sie mehr über den Datenschutz für IONOS Website erfahren möchten,
      besuchen Sie bitte die
      <a
        class="adsimple-311825881"
        href="https://www.ionos.de/terms-gtc/terms-privacy?tid=311825881"
        target="_blank"
        rel="follow noopener"
        >Datenschutzerklärung auf ionos.de</a
      >.
    </p>
    <h2 id="cookie-consent-management-platform" class="adsimple-311825881">
      Cookie Consent Management Platform
    </h2>
    <table border="1" cellpadding="15">
      <tbody>
        <tr>
          <td>
            <strong class="adsimple-311825881"
              >Cookie Consent Management Platform Zusammenfassung</strong
            >
            <br />
            &#x1f465; Betroffene: Website Besucher<br />
            &#x1f91d; Zweck: Einholung und Verwaltung der Zustimmung zu
            bestimmten Cookies und somit dem Einsatz bestimmter Tools<br />
            &#x1f4d3; Verarbeitete Daten: Daten zur Verwaltung der eingestellten
            Cookie-Einstellungen wie IP-Adresse, Zeitpunkt der Zustimmung, Art
            der Zustimmung, einzelne Zustimmungen. Mehr Details dazu finden Sie
            beim jeweils eingesetzten Tool.<br />
            &#x1f4c5; Speicherdauer: Hängt vom eingesetzten Tool ab, man muss
            sich auf Zeiträume von mehreren Jahren einstellen<br />
            &#x2696;&#xfe0f; Rechtsgrundlagen: Art. 6 Abs. 1 lit. a DSGVO
            (Einwilligung), Art. 6 Abs. 1 lit.f DSGVO (berechtigte Interessen)
          </td>
        </tr>
      </tbody>
    </table>
    <h3 class="adsimple-311825881">
      Was ist eine Cookie Consent Manangement Platform?
    </h3>
    <p>
      Wir verwenden auf unserer Website eine Consent Management Platform (CMP)
      Software, die uns und Ihnen den korrekten und sicheren Umgang mit
      verwendeten Skripten und Cookies erleichtert. Die Software erstellt
      automatisch ein Cookie-Popup, scannt und kontrolliert alle Skripts und
      Cookies, bietet eine datenschutzrechtlich notwendige Cookie-Einwilligung
      für Sie und hilft uns und Ihnen den Überblick über alle Cookies zu
      behalten. Bei den meisten Cookie Consent Management Tools werden alle
      vorhandenen Cookies identifiziert und kategorisiert. Sie als
      Websitebesucher entscheiden dann selbst, ob und welche Skripte und Cookies
      Sie zulassen oder nicht zulassen. Die folgende Grafik stellt die Beziehung
      zwischen Browser, Webserver und CMP dar.
    </p>
    <p>
      <img
        role="img"
        src="https://www.adsimple.at/wp-content/uploads/2021/03/consent-management-platform-overview.svg"
        alt="Consent Management Platform Überblick"
        width="100%"
      />
    </p>
    <h3 class="adsimple-311825881">
      Warum verwenden wir ein Cookie-Management-Tool?
    </h3>
    <p>
      Unser Ziel ist es, Ihnen im Bereich Datenschutz die bestmögliche
      Transparenz zu bieten. Zudem sind wir dazu auch rechtlich verpflichtet.
      Wir wollen Sie über alle Tools und alle Cookies, die Daten von Ihnen
      speichern und verarbeiten können, so gut wie möglich aufklären. Es ist
      auch Ihr Recht, selbst zu entscheiden, welche Cookies Sie akzeptieren und
      welche nicht. Um Ihnen dieses Recht einzuräumen, müssen wir zuerst genau
      wissen, welche Cookies überhaupt auf unserer Website gelandet sind. Dank
      eines Cookie-Management-Tools, welches die Website regelmäßig nach allen
      vorhandenen Cookies scannt, wissen wir über alle Cookies Bescheid und
      können Ihnen DSGVO-konform Auskunft darüber geben. Über das
      Einwilligungssystem können Sie dann Cookies akzeptieren oder ablehnen.
    </p>
    <h3 class="adsimple-311825881">Welche Daten werden verarbeitet?</h3>
    <p>
      Im Rahmen unseres Cookie-Management-Tools können Sie jedes einzelnen
      Cookies selbst verwalten und haben die vollständige Kontrolle über die
      Speicherung und Verarbeitung Ihrer Daten. Die Erklärung Ihrer Einwilligung
      wird gespeichert, damit wir Sie nicht bei jedem neuen Besuch unserer
      Website abfragen müssen und wir Ihre Einwilligung, wenn gesetzlich nötig,
      auch nachweisen können. Gespeichert wird dies entweder in einem
      Opt-in-Cookie oder auf einem Server. Je nach Anbieter des
      Cookie-Management-Tools variiert Speicherdauer Ihrer Cookie-Einwilligung.
      Meist werden diese Daten (etwa pseudonyme User-ID,
      Einwilligungs-Zeitpunkt, Detailangaben zu den Cookie-Kategorien oder
      Tools, Browser, Gerätinformationen) bis zu zwei Jahren gespeichert.
    </p>
    <h3 class="adsimple-311825881">Dauer der Datenverarbeitung</h3>
    <p>
      Über die Dauer der Datenverarbeitung informieren wir Sie weiter unten,
      sofern wir weitere Informationen dazu haben. Generell verarbeiten wir
      personenbezogene Daten nur so lange wie es für die Bereitstellung unserer
      Dienstleistungen und Produkte unbedingt notwendig ist. Daten, die in
      Cookies gespeichert werden, werden unterschiedlich lange gespeichert.
      Manche Cookies werden bereits nach dem Verlassen der Website wieder
      gelöscht, anderen können über einige Jahre in Ihrem Browser gespeichert
      sein. Die genaue Dauer der Datenverarbeitung hängt vom verwendeten Tool
      ab, meistens sollten Sie sich auf eine Speicherdauer von mehreren Jahren
      einstellen. In den jeweiligen Datenschutzerklärungen der einzelnen
      Anbieter erhalten Sie in der Regel genaue Informationen über die Dauer der
      Datenverarbeitung.
    </p>
    <h3 class="adsimple-311825881">Widerspruchsrecht</h3>
    <p>
      Sie haben auch jederzeit das Recht und die Möglichkeit Ihre Einwilligung
      zur Verwendung von Cookies zu widerrufen. Das funktioniert entweder über
      unser Cookie-Management-Tool oder über andere Opt-Out-Funktionen. Zum
      Bespiel können Sie auch die Datenerfassung durch Cookies verhindern, indem
      Sie in Ihrem Browser die Cookies verwalten, deaktivieren oder löschen.
    </p>
    <p>
      Informationen zu speziellen Cookie-Management-Tools, erfahren Sie &#8211;
      sofern vorhanden &#8211; in den folgenden Abschnitten.
    </p>
    <h3 class="adsimple-311825881">Rechtsgrundlage</h3>
    <p>
      Wenn Sie Cookies zustimmen, werden über diese Cookies personenbezogene
      Daten von Ihnen verarbeitet und gespeichert. Falls wir durch Ihre
      <strong class="adsimple-311825881">Einwilligung</strong> (Artikel 6 Abs. 1
      lit. a DSGVO) Cookies verwenden dürfen, ist diese Einwilligung auch
      gleichzeitig die Rechtsgrundlage für die Verwendung von Cookies bzw. die
      Verarbeitung Ihrer Daten. Um die Einwilligung zu Cookies verwalten zu
      können und Ihnen die Einwilligung ermöglichen zu können, kommt eine
      Cookie-Consent-Management-Platform-Software zum Einsatz. Der Einsatz
      dieser Software ermöglicht uns, die Website auf effiziente Weise
      rechtskonform zu betreiben, was ein
      <strong class="adsimple-311825881">berechtigtes Interesse</strong>
      (Artikel 6 Abs. 1 lit. f DSGVO) darstellt.
    </p>
    <p>Alle Texte sind urheberrechtlich geschützt.</p>
    <p style="margin-top: 15px">
      Quelle: Erstellt mit dem
      <a
        href="https://www.adsimple.de/datenschutz-generator/"
        title="Datenschutz Generator von AdSimple für Deutschland"
        >Datenschutz Generator</a
      >
      von AdSimple
    </p>
  </div>
</template>

<script>
export default {
  name: "Datenschutz",
};
</script>

<style scoped></style>
